import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { payWithRazorpay } from "@/lib/razorpay";
import { PackageCheck, FileText, Loader2, RefreshCcw, ArrowRight, Download, RotateCcw, CreditCard, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

export default function UserDashboard() {
  const { user, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revisionFor, setRevisionFor] = useState(null);
  const [revisionText, setRevisionText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/orders/mine");
      setOrders(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const summary = {
    total: orders.length,
    pending: orders.filter((o) => !["Delivered", "Final document delivered"].includes(o.status)).length,
    delivered: orders.filter((o) => ["Delivered", "Final document delivered"].includes(o.status)).length,
    files: orders.reduce((n, o) => n + (o.details?.file_ids?.length || 0), 0),
  };

  const downloadFile = async (fid) => {
    try {
      const resp = await api.get(`/files/${fid}/download`, { responseType: "blob" });
      const cd = resp.headers?.["content-disposition"] || "";
      const match = cd.match(/filename="?([^";]+)"?/i);
      const filename = match ? match[1] : `deliverable-${fid.slice(0, 6)}`;
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const submitRevision = async () => {
    if (!revisionText.trim()) return toast.error("Please describe what you'd like revised.");
    setSubmitting(true);
    try {
      await api.post(`/orders/${revisionFor.id}/revision`, { reason: revisionText });
      toast.success("Revision request sent. We'll get on it.");
      setRevisionFor(null);
      setRevisionText("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="user-dashboard" className="container-app py-12">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="section-eyebrow">Dashboard</span>
          <h1 className="font-display text-4xl text-navy mt-2">Hi, {user?.name}</h1>
          <div className="text-sm text-slate-500">{user?.email}</div>
        </div>
        <div className="flex gap-2">
          <Link to="/upload" className="btn-gold">New printing order</Link>
          <Link to="/academic-request" className="btn-outline">New academic request</Link>
          <button onClick={logout} className="btn-outline">Log out</button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
        <Kpi label="Total orders" value={summary.total} testid="kpi-total" />
        <Kpi label="Pending" value={summary.pending} testid="kpi-pending" />
        <Kpi label="Delivered" value={summary.delivered} testid="kpi-delivered" />
        <Kpi label="Uploaded files" value={summary.files} testid="kpi-files" />
      </div>

      <div className="mt-10 flex items-center justify-between">
        <h2 className="font-display text-2xl text-navy">Your orders</h2>
        <button onClick={load} className="text-xs text-royal hover:underline flex items-center gap-1"><RefreshCcw className="w-3 h-3" /> Refresh</button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-navy" /></div>
      ) : orders.length === 0 ? (
        <div className="border border-dashed border-slate-300 rounded-sm p-10 text-center mt-4">
          <FileText className="w-8 h-8 mx-auto text-slate-400" />
          <div className="mt-3 text-slate-500 text-sm">No orders yet.</div>
          <div className="mt-4 flex gap-2 justify-center">
            <Link to="/upload" className="btn-primary">Upload PDF</Link>
            <Link to="/academic-request" className="btn-outline">Request academic help</Link>
          </div>
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          {orders.map((o) => (
            <div key={o.id} data-testid={`user-order-${o.order_number}`} className="border border-slate-200 rounded-sm p-5 bg-white">
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex-1">
                  <div className="font-mono text-xs text-slate-500">{o.type === "print" ? "Printing" : "Academic"}</div>
                  <div className="font-display text-xl text-navy">{o.order_number}</div>
                  <div className="text-xs text-slate-500">{new Date(o.created_at).toLocaleString("en-IN")}</div>
                </div>
                <div className="flex-1">
                  <div className="text-xs text-slate-500">Current status</div>
                  <div className="font-medium text-navy flex items-center gap-2"><PackageCheck className="w-4 h-4 text-gold" /> {o.status}</div>
                  <div className="text-xs text-slate-500 mt-1">Payment: {o.payment_status}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-royal">{o.quotation ? `₹${o.quotation}` : "Quote pending"}</div>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-slate-100">
                {o.quotation && o.payment_status !== "paid" && (
                  <button
                    onClick={() => payWithRazorpay(o.id, () => load())}
                    data-testid={`pay-btn-${o.order_number}`}
                    className="btn-primary !py-2 !text-xs"
                  >
                    <CreditCard className="w-4 h-4" /> Pay ₹{o.quotation}
                  </button>
                )}
                {(o.deliverables || []).length > 0 && (o.deliverables || []).map((fid) => (
                  <button
                    key={fid}
                    onClick={() => downloadFile(fid)}
                    data-testid={`download-deliverable-${fid}`}
                    className="btn-gold !py-2 !text-xs"
                  >
                    <Download className="w-4 h-4" /> Download final document
                  </button>
                ))}
                {(o.deliverables || []).length > 0 && o.status !== "Revision requested" && (
                  <button
                    onClick={() => setRevisionFor(o)}
                    data-testid={`revision-btn-${o.order_number}`}
                    className="btn-outline !py-2 !text-xs"
                  >
                    <RotateCcw className="w-4 h-4" /> Request revision
                  </button>
                )}
                <Link to="/track" className="text-xs text-royal hover:underline inline-flex items-center gap-1 ml-auto">
                  View timeline <ArrowRight className="w-3 h-3" />
                </Link>
              </div>

              {(o.revision_requests || []).length > 0 && (
                <div className="mt-4 border-l-2 border-gold bg-slate-50 p-3 text-xs">
                  <div className="section-eyebrow mb-1">Revision requests</div>
                  {o.revision_requests.map((r) => (
                    <div key={r.id} className="text-slate-700 mt-1">• {r.reason} <span className="text-slate-400">({new Date(r.at).toLocaleDateString("en-IN")})</span></div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Revision dialog */}
      <Dialog open={!!revisionFor} onOpenChange={(v) => { if (!v) { setRevisionFor(null); setRevisionText(""); } }}>
        <DialogContent data-testid="revision-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Request a revision · {revisionFor?.order_number}</DialogTitle>
          </DialogHeader>
          <div className="mt-2">
            <Label className="text-xs">What should we revise?</Label>
            <Textarea rows={5} value={revisionText} onChange={(e) => setRevisionText(e.target.value)} data-testid="revision-text" placeholder="Please describe the changes you'd like — reference chapter names, page numbers, or specific sections." />
          </div>
          <DialogFooter>
            <button onClick={() => setRevisionFor(null)} className="btn-outline"><X className="w-4 h-4" /> Cancel</button>
            <button onClick={submitRevision} disabled={submitting} data-testid="revision-submit" className="btn-primary">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />} Send request
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kpi({ label, value, testid }) {
  return (
    <div data-testid={testid} className="border border-slate-200 rounded-sm p-5 bg-white">
      <div className="section-eyebrow">{label}</div>
      <div className="font-display text-4xl text-navy mt-2">{value}</div>
    </div>
  );
}
